import type { CustomerUserView } from '@api/customers/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Mail, UserPlus, UserRound } from 'lucide-react';
import { type FormEvent, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { addCustomerUser, isStatus, listCustomerUsers, resendCustomerUserSetup, resetCustomerUser, searchRecords, setCustomerUserActive, setCustomerUserDevices } from '../lib/api';
import { useScope } from '../scope/scope';
import { useOwnsEveryCustomer } from './AddCustomer';
import { Button } from '../ui/Button';
import { Card } from '../ui/misc';
import { useToast } from '../ui/toast';

const field = 'h-9 w-full rounded-lg border border-line bg-panel px-2.5 text-[13.5px] outline-none focus:border-brand/50';

/** The API schema line this picker mirrors: `deviceIds` in apps/api/src/customers/customer-users.ts, `z.array(z.uuid()).max(200)`. */
const MAX_DEVICES_PER_END_USER = 200;
const deviceLimitMessage = `Up to ${MAX_DEVICES_PER_END_USER} computers per person. Unselect one to add another.`;

/**
 * Where someone is with setup, in a few words: an owner may add them now and send the setup email later (WRMM-220). Once
 * one was made it says no more than "not set up yet": whether the mail service took it is known only at the time.
 */
export const endUserStatus = (u: CustomerUserView) => u.setUp ? 'set up' : u.setupEmailSent ? 'not set up yet' : 'not set up, no setup email sent yet';

/** Which of this customer's devices someone sees: a checkbox per device. */
function DevicePicker({ devices, chosen, onChange }: { devices: Array<{ id: string; title: string }> | undefined; chosen: Set<string>; onChange: (next: Set<string>) => void }) {
  if (!devices) return <p className="text-[13px] text-ink-3">Loading this customer’s computers…</p>;
  if (!devices.length) return <p className="text-[13px] text-ink-3">This customer has no devices yet.</p>;
  const atLimit = chosen.size >= MAX_DEVICES_PER_END_USER;
  return (
    <fieldset className="grid gap-1.5 sm:grid-cols-2">
      <legend className="mb-1 text-[13px] font-medium">Computers they see</legend>
      {atLimit && <p className="text-[13px] text-ink-3 sm:col-span-2">{deviceLimitMessage}</p>}
      {devices.map(d => {
        const checked = chosen.has(d.id);
        return (
          <label key={d.id} className="flex items-center gap-2 text-[13.5px]">
            <input type="checkbox" checked={checked} disabled={!checked && atLimit} onChange={e => {
              const next = new Set(chosen);
              if (e.target.checked) { if (next.size >= MAX_DEVICES_PER_END_USER) return; next.add(d.id); }
              else next.delete(d.id);
              onChange(next);
            }} />
            {d.title}
          </label>
        );
      })}
    </fieldset>
  );
}

/**
 * A customer's end users, who see their own computers (WRMM-134; Harry, 2026-10-23: "i can select devices per customer
 * that a person can see"). Workspace owners, and technicians who manage this customer's end users (WRMM-374), add them by email, choose up to 200 of their computers, send or resend the setup email
 * (which takes them through setup), or turn them off; only owners choose their computers, turn back on someone who has
 * computers, or reset them (password and 2FA): an end user controls their computers with no key touch. Adding someone sends no email unless the owner ticks
 * it (WRMM-220: Harry sends it during the customer's onboarding). They sign in with a password and a second factor and
 * see only those computers. `?person=<id>` (from the End users page) opens that person's computers for an owner; for anyone else it scrolls to them.
 */
export function CustomerUsers({ organizationId, name }: { organizationId: string; name?: string }) {
  const { mspId } = useScope();
  const owner = useOwnsEveryCustomer();
  const client = useQueryClient();
  const toast = useToast();
  const key = ['scope', mspId, 'customer-users', organizationId];
  const users = useQuery({ queryKey: key, queryFn: ({ signal }) => listCustomerUsers(mspId, organizationId, signal) });
  // Every device of the customer, a page of 100 at a time: the picker must offer all of them, not just the first page.
  const found = useQuery({ queryKey: ['scope', mspId, 'customer-devices', organizationId], staleTime: 30_000, queryFn: async ({ signal }) => {
    const all: Array<{ id: string; title: string }> = [];
    for (let offset = 0; offset <= 10_000; offset += 100) {
      const page = await searchRecords(mspId, { version: 1, kinds: ['device'], limit: 100, offset,
        filters: [{ mode: 'and', clauses: [{ field: 'organizationId', op: 'in', values: [organizationId] }] }] }, signal);
      all.push(...page.hits.map(h => ({ id: h.id, title: h.title })));
      if (page.hits.length < 100 || all.length >= page.total) break;
    }
    return all;
  } });
  // Undefined until every page has loaded: nothing is saved against a partial or failed list.
  const devices = found.data ? [...found.data].sort((a, b) => a.title.localeCompare(b.title)) : undefined;
  const [adding, setAdding] = useState(false);
  const [personName, setPersonName] = useState('');
  const [email, setEmail] = useState('');
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const [sendNow, setSendNow] = useState(false);
  const [editing, setEditing] = useState<{ id: string; chosen: Set<string> } | null>(null);
  const [params, setParams] = useSearchParams();
  const opened = useRef<HTMLLIElement | null>(null);
  const jump = useRef(false); // Brought into view once it opens, for a person opened from the End users page only.
  const asked = params.get('person');
  useEffect(() => {
    const user = asked && users.data?.find(u => u.id === asked);
    if (!user) return;
    setEditing({ id: user.id, chosen: new Set(user.devices.map(d => d.id)) });
    jump.current = true;
    setParams(p => { p.delete('person'); return p; }, { replace: true });
  }, [asked, users.data, setParams]);
  useEffect(() => { if (jump.current && opened.current) { jump.current = false; opened.current.scrollIntoView?.({ block: 'center' }); } });
  const done = (title: string) => (user: CustomerUserView) => {
    // This customer's list and the End users page.
    void client.invalidateQueries({ queryKey: ['scope', mspId, 'customer-users'] });
    toast(user.emailSent === false ? { tone: 'warning', title: 'Saved, but the email wasn’t sent', body: 'Try Resend setup email in a minute.' } : { title });
  };
  const failed = (title: string, taken = 'That email already has an account.') => (error: unknown) => toast({ tone: 'warning', title, body: isStatus(error, 409) ? taken
    : isStatus(error, 503) ? 'Email isn’t set up on this server yet.'
    : isStatus(error, 403) ? 'You can’t do that for this end user; ask an owner.' : isStatus(error, 400) ? 'Check the name, email and computers.' : 'Try again.' });
  const add = useMutation({ mutationFn: () => addCustomerUser(mspId, { organizationId, name: personName.trim(), email: email.trim(), deviceIds: [...chosen], sendSetupEmail: sendNow }),
    onSuccess: user => { done(sendNow ? `Setup email sent to ${user.email}` : `${user.email} added; no setup email sent yet`)(user); setAdding(false); setPersonName(''); setEmail(''); setChosen(new Set()); setSendNow(false); },
    onError: failed('They weren’t added') });
  const act = useMutation({ mutationFn: (run: () => Promise<CustomerUserView>) => run(), onError: failed('That didn’t work', 'They’re already set up: use Reset instead.') });
  const submit = (e: FormEvent) => { e.preventDefault(); add.mutate(); };
  return (
    <Card className="p-5">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="min-w-0 flex-1 text-[15px] font-semibold [overflow-wrap:anywhere]">End users at {name ?? 'this customer'}</h2>
        {!adding && <Button size="sm" onClick={() => setAdding(true)}><UserPlus className="size-4" /> Add an end user</Button>}
      </div>
      <p className="mt-1 text-[13px] text-ink-2">They sign in with their email, a password and a second factor, and see only the computers you choose here. The setup email starts that; send it when they’re ready.</p>
      {adding && (
        <form onSubmit={submit} className="mt-4 grid gap-3 rounded-lg border border-line p-4">
          <label className="text-[13px] font-medium">Name<input className={`${field} mt-1`} value={personName} maxLength={120} onChange={e => setPersonName(e.target.value)} /></label>
          <label className="text-[13px] font-medium">Email<input type="email" className={`${field} mt-1`} value={email} maxLength={254} onChange={e => setEmail(e.target.value)} /></label>
          {owner ? <DevicePicker devices={devices} chosen={chosen} onChange={setChosen} />
            : <p className="text-[13px] text-ink-2">An owner chooses which computers they see.</p>}
          <label className="flex items-center gap-2 text-[13.5px]"><input type="checkbox" checked={sendNow} onChange={e => setSendNow(e.target.checked)} />Send the setup email now</label>
          <div className="flex gap-2">
            {found.isError && <p role="alert" className="text-[13px] text-attention">This customer’s computers couldn’t be loaded, so nobody can be added yet.</p>}
            <Button type="submit" variant="primary" size="sm" disabled={add.isPending || !devices || !personName.trim() || !email.trim() || chosen.size > MAX_DEVICES_PER_END_USER}>{sendNow ? <><Mail className="size-4" /> Add and send setup email</> : <><UserPlus className="size-4" /> Add end user</>}</Button>
            <Button type="button" variant="ghost" size="sm" onClick={() => setAdding(false)}>Cancel</Button>
          </div>
        </form>
      )}
      <ul className="mt-4 divide-y divide-line" aria-label={`End users at ${name ?? 'this customer'}`}>
        {users.isError && <li role="alert" className="py-2 text-[13px] text-attention">End users couldn’t be loaded.</li>}
        {users.data?.length === 0 && <li className="py-2 text-[13px] text-ink-3">Nobody yet.</li>}
        {users.data?.map(u => (
          <li key={u.id} className="py-3" ref={editing?.id === u.id ? opened : undefined}>
            <div className="flex flex-wrap items-center gap-3">
              <UserRound className="size-4 text-ink-3" />
              <div className="min-w-0 flex-1">
                <p className="font-medium">{u.name ?? u.email}{!u.active && <span className="ml-2 text-[12px] text-attention">turned off</span>}</p>
                <p className="text-[12.5px] text-ink-3">{u.email} · {endUserStatus(u)} · {u.devices.length
                  ? u.devices.map(d => d.hostname).join(', ') : 'no computers'}</p>
              </div>
              {owner && <Button size="sm" variant="ghost" onClick={() => setEditing({ id: u.id, chosen: new Set(u.devices.map(d => d.id)) })}>Computers</Button>}
              {!u.setUp && <Button size="sm" variant="ghost" disabled={act.isPending} onClick={() => act.mutate(() => resendCustomerUserSetup(mspId, u.id),
                { onSuccess: done(u.setupEmailSent ? 'Setup email sent again' : `Setup email sent to ${u.email}`) })}>{u.setupEmailSent ? 'Resend setup email' : 'Send setup email'}</Button>}
              {u.setUp && owner && <Button size="sm" variant="ghost" disabled={act.isPending} onClick={() => act.mutate(() => resetCustomerUser(mspId, u.id), { onSuccess: done(`Reset: ${u.email} gets a new setup email`) })}>Reset</Button>}
              {(u.active || owner || !u.devices.length) && <Button size="sm" variant="ghost" disabled={act.isPending} onClick={() => act.mutate(() => setCustomerUserActive(mspId, u.id, !u.active), { onSuccess: done(u.active ? 'Turned off' : 'Turned on') })}>{u.active ? 'Turn off' : 'Turn on'}</Button>}
            </div>
            {owner && editing?.id === u.id && (
              <div className="mt-3 grid gap-3 rounded-lg border border-line p-4">
                <DevicePicker devices={devices} chosen={editing.chosen} onChange={next => setEditing({ id: u.id, chosen: next })} />
                <div className="flex gap-2">
                  <Button size="sm" variant="primary" disabled={act.isPending || !devices || editing.chosen.size > MAX_DEVICES_PER_END_USER} onClick={() => act.mutate(() => setCustomerUserDevices(mspId, u.id, [...editing.chosen]),
                    { onSuccess: user => { done('Computers saved')(user); setEditing(null); } })}>Save</Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
                </div>
              </div>
            )}
          </li>
        ))}
      </ul>
    </Card>
  );
}
